import React, { useState } from 'react'
import { Swiper } from 'swiper/react'
import { Navigation } from 'swiper'

type DashboardSliderProps = {
  children: ReactNode,
  defaultItem: Int,
  details?: ReactNode,
  onActiveIndexChange?: () => void,
  onClick?: () => void,
  onTap?: () => void,
  onSliderMove?: () => void,
  spaceBetween?: Int,
  className?: String,
  swiperProps?: Object,
  width?: String,
  slidesPerView?: Number,
  loop?: Boolean,
  centeredSlides?: Boolean,
  navigation?: Boolean,
  rewind?: Boolean,
}

DashboardSlider.defaultProps = {
  details: undefined,
  onActiveIndexChange: undefined,
  onClick: undefined,
  onTap: undefined,
  onSliderMove: undefined,
  spaceBetween: -8,
  className: undefined,
  swiperProps: {},
  width: undefined,
  slidesPerView: 1.25,
  loop: true,
  centeredSlides: true,
  navigation: true,
  rewind: true,
}

/**
 * DashboardSlider component renders a Swiper slider with various customizable properties.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode[]} props.children - The slides to be displayed in the slider.
 * @param {number} [props.defaultItem] - The index of the default slide to be displayed initially.
 * @param {React.ReactNode} [props.details] - Additional details to be displayed alongside the slider.
 * @param {function} [props.onActiveIndexChange] - Callback function triggered when the active slide index changes.
 * @param {function} [props.onClick] - Callback function triggered when a slide is clicked.
 * @param {function} [props.onTap] - Callback function triggered when a slide is tapped.
 * @param {function} [props.onSliderMove] - Callback function triggered when the slider is moved.
 * @param {number} [props.spaceBetween] - The space between slides in pixels.
 * @param {string} [props.className] - Additional class names for the slider container.
 * @param {Object} [props.swiperProps] - Additional properties to be passed to the Swiper component.
 * @param {number} [props.width] - The width of each slide.
 * @param {number} [props.slidesPerView] - The number of slides to be displayed per view.
 * @param {boolean} [props.loop] - Whether the slider should loop infinitely.
 * @param {boolean} [props.centeredSlides] - Whether the slides should be centered.
 * @param {boolean} [props.navigation] - Whether navigation buttons should be displayed.
 * @param {boolean} [props.rewind] - Whether the slider should rewind to the beginning when it reaches the end.
 *
 * @returns {JSX.Element} The rendered DashboardSlider component.
 */
function DashboardSlider(props: DashboardSliderProps) {
  const {
    children,
    defaultItem,
    details,
    onActiveIndexChange,
    onClick,
    onTap,
    onSliderMove,
    spaceBetween,
    className,
    swiperProps,
    width,
    slidesPerView,
    loop,
    centeredSlides,
    navigation,
    rewind,
  } = props
  const [swiperSlideWidth, setSwiperSlideWidth] = useState(undefined)

  const bigScreenSlidesNum = 1.5

  const filteredChildren = children.filter(
    value => value !== null && value !== undefined,
  )

  // Note that to work around an issue in swiperjs we have to actually have #slides >= slidesPerView for loop mode to work
  return (
    <div className={`dashboard-slider ${className || ''}`}>
      <Swiper
        modules={[Navigation]}
        onInit={swip => {
          if (swip.slides.length === 0) return
          setSwiperSlideWidth(swip.slides[swip.realIndex].clientWidth)
          if (onActiveIndexChange) onActiveIndexChange(swip)
        }}
        onResize={swip => {
          if (swip.slides.length === 0) return
          setSwiperSlideWidth(swip.slides[swip.realIndex].clientWidth)
        }}
        onActiveIndexChange={swip => {
          if (onActiveIndexChange) onActiveIndexChange(swip)
        }}
        onSliderMove={swip => {
          if (onSliderMove) onSliderMove(swip)
        }}
        onClick={(swip, event) => {
          if (onClick) onClick(swip, event)
        }}
        onTap={(swip, event) => {
          if (onTap) onTap(swip, event)
        }}
        navigation={navigation}
        centeredSlides={centeredSlides}
        loop={loop && filteredChildren.length >= bigScreenSlidesNum * 2}
        rewind={!loop && rewind}
        spaceBetween={spaceBetween}
        initialSlide={defaultItem > 0 ? defaultItem : 0}
        slidesPerView={slidesPerView}
        breakpoints={
          swiperProps.breakpoints || {
            1024: {
              slidesPerView: bigScreenSlidesNum,
            },
          }
        }
        className="dashboard-slider--swiper"
        style={{
          '--dashboard-slider--slide-width': `${width || swiperSlideWidth}px`,
          overflow: 'visible',
        }}
      >
        {filteredChildren}
      </Swiper>
      {details && (
        <div className="flex--auto-space dashboard-slider--details">
          <div
            style={{
              width: `${width || swiperSlideWidth}px` || '100%',
            }}
          >
            {details}
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardSlider
